import { gql, useQuery } from '@apollo/client';
import { PropsWithChildren, createContext, useContext } from 'react';

import { IBasicAddressWithTimezone } from '../../utils/addresses';
import { useQueryParams } from '../../utils/queryParams';
import { useAddressForVisitBookingFlow } from './useAddressForVisitBookingFlow';
import {
    VisitRequestForVisitBookingFlow,
    useVisitRequestForVisitBookingFlow,
} from './useVisitRequestForVisitBookingFlow';

export const VISIT_BOOKING_INFO_PAGE = 'info';
export const VISIT_BOOKING_SCHEDULING_PAGE = 'scheduling';

const PATIENT_ORGANIZATION_QUERY = gql`
    query PatientOrganization($id: UUID!) {
        patient(id: $id) {
            id
            organization {
                id
            }
        }
    }
`;

type VisitBookingPageContextType = {
    patientId: string;
    demandOrgId: string;
    address: IBasicAddressWithTimezone | null;
    serviceAreaId: string;
    serviceBundleId: string;
    visitRequest: VisitRequestForVisitBookingFlow | null;
};
const VisitBookingPageContext = createContext<VisitBookingPageContextType | undefined>(undefined);

export const VisitBookingPageProvider = ({ children }: PropsWithChildren) => {
    // Atoms for address, service area, and service bundle selection so that they persist
    // across page reloads and don't kick the user back to the info page if they're already
    // on the scheduling page.
    const [{ patientId }] = useQueryParams({ patientId: '' });
    const [{ serviceAreaId }] = useQueryParams({ serviceAreaId: '' });
    const [{ serviceBundleId }] = useQueryParams({ serviceBundleId: '' });
    const { data } = useQuery<{
        patient: {
            organization: { id: string };
        } | null;
    }>(PATIENT_ORGANIZATION_QUERY, { variables: { id: patientId } });
    const demandOrgId = data?.patient?.organization.id ?? '';
    const address = useAddressForVisitBookingFlow();
    const visitRequest = useVisitRequestForVisitBookingFlow();

    return (
        <VisitBookingPageContext.Provider
            value={{
                address,
                patientId,
                demandOrgId,
                serviceAreaId,
                serviceBundleId,
                visitRequest,
            }}
        >
            {children}
        </VisitBookingPageContext.Provider>
    );
};

export const useVisitBookingPageContext = () => {
    const context = useContext(VisitBookingPageContext);
    if (context === undefined) {
        throw new Error(
            'useVisitBookingPageContext must be used within a VisitBookingPageProvider',
        );
    }
    return context;
};
